import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@fragments";
import { IconUserPlus } from "@tabler/icons-react";

import useMentoringPrograms from "./hooks/useMentoringPrograms";
import useCommunityCreatorMentorings from "./hooks/useCommunityCreatorMentorings";
import MentoringProgramsView from "./view";
import ProgramEditorModal from "./view/components/ProgramEditorModal";
import InviteModal from "./view/components/InviteModal";
import ImportModal from "./view/components/ImportModal";
import MentoringEnrollments from "./enrollments";
import MentoringEnrollmentDetails from "./enrollments/details";

/**
 * Mentoria tab controller — mirrors the Forms tab (tab → list → detail).
 * Route slots (see src/routes.js): :slug/settings/mentoring/:subtab/:itemId
 *   - no subtab               → programs list (§6.1)
 *   - subtab = programId      → program enrollments (§6.3)
 *   - + itemId = enrollmentId → enrollment detail (§6.4)
 */
export default function Mentoring({ setPageActions }) {
  const { subtab, itemId } = useParams();

  if (subtab && itemId) {
    return <MentoringEnrollmentDetails setPageActions={setPageActions} />;
  }

  if (subtab) {
    return <MentoringEnrollments setPageActions={setPageActions} />;
  }

  return <MentoringPrograms setPageActions={setPageActions} />;
}

// Default view: programs list. Kept as its own component so the hook is never
// called conditionally when the controller switches to enrollments/detail.
function MentoringPrograms({ setPageActions }) {
  const { programs, loading, refresh, currentCommunity, setProgramActive, deleteProgram, upsertProgram } =
    useMentoringPrograms();
  const editorRef = useRef({});
  const inviteRef = useRef({});
  const importRef = useRef({});

  const communityId = currentCommunity?.id;

  // This community may itself be a mentored creator in another owner's program.
  const { mentorings: creatorMentorings } = useCommunityCreatorMentorings(communityId);

  function openEditor(program) {
    // Merge the saved program for instant feedback, then reload the list so
    // server-computed fields (counts/metrics) are fresh.
    editorRef.current.open?.(program ?? null, (saved) => {
      upsertProgram(saved);
      refresh();
    });
  }

  useEffect(() => {
    if (!setPageActions) return;
    setPageActions(
      <Button
        text="Criar programa"
        endIcon={<IconUserPlus />}
        color="primary"
        size="small"
        onClick={() => openEditor(null)}
      />
    );
    return () => setPageActions(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setPageActions]);

  return (
    <>
      <MentoringProgramsView
        programs={programs}
        loading={loading}
        creatorMentorings={creatorMentorings}
        onCreateProgram={() => openEditor(null)}
        onEditProgram={(program) => openEditor(program)}
        onSetActive={setProgramActive}
        onDeleteProgram={deleteProgram}
        onInvite={(program) => inviteRef.current.open?.(program, refresh)}
        onImport={(program) => importRef.current.open?.(program, refresh)}
      />
      <ProgramEditorModal modalRef={editorRef} community={currentCommunity} />
      <InviteModal modalRef={inviteRef} communityId={communityId} />
      <ImportModal modalRef={importRef} communityId={communityId} />
    </>
  );
}
